// 사용자 인증 상태를 관리하는 Context Provider — poc-portal AuthContext 이식판
import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import authService, { REFRESH_KEY, TOKEN_KEY, onSessionExpired } from '../api/auth';
import type { Credentials } from '../api/auth';
import type { User } from '../types';

interface AuthContextValue {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  /** 세션 복구 등 초기 로딩 여부 */
  initializing: boolean;
  /** 리프레시까지 만료돼 재로그인 안내가 필요한 상태 */
  sessionExpired: boolean;
  /** 안내 다이얼로그의 확인 처리 */
  dismissSessionExpired: () => void;
  loginWithCredentials: (creds: Credentials) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  // 새로고침 시 저장된 토큰으로 세션 복구 — access가 없어도 refresh가 있으면 재발급을 시도한다
  useEffect(() => {
    const saved = localStorage.getItem(TOKEN_KEY);
    if (!saved && !localStorage.getItem(REFRESH_KEY)) {
      setInitializing(false);
      return;
    }
    authService
      .restoreSession(saved)
      .then((result) => {
        // access가 없거나 만료돼 refresh로 재발급됐을 수 있으므로 결과 토큰을 다시 저장
        localStorage.setItem(TOKEN_KEY, result.token);
        setUser(result.user);
        setToken(result.token);
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
      })
      .finally(() => setInitializing(false));
  }, []);

  /* 리프레시 토큰까지 만료돼 재로그인이 필요해진 경우 — 공용 유틸이 토큰을 비우고 이 이벤트를 던진다.
     호스트는 라우터가 없어 isAuthenticated 분기(App.tsx)가 곧 화면이므로, 상태만 비우면 로그인 화면이 뜬다.
     확인을 기다리지 않고 바로 전환하고, 안내 다이얼로그는 그 위에 띄운다(SessionExpiredDialog).
     모듈(board·onechamber)은 별도 번들이라 같은 window 이벤트로 만료를 올려보낸다. */
  useEffect(() => onSessionExpired(() => {
    setToken(null);
    setUser(null);
    setSessionExpired(true);
  }), []);

  const dismissSessionExpired = () => setSessionExpired(false);

  const applyResult = (result: { token: string; user: User }) => {
    localStorage.setItem(TOKEN_KEY, result.token);
    setToken(result.token);
    setUser(result.user);
  };

  const loginWithCredentials = async (creds: Credentials) => {
    const result = await authService.loginWithCredentials(creds);
    applyResult(result);
  };

  const logout = async () => {
    await authService.logout();
    localStorage.removeItem(TOKEN_KEY);
    /* 저장된 자격증명(AUTO_LOGIN_KEY)은 유지 — 로그인 화면에서 자동 입력만 하고
       자동 제출은 하지 않으므로 로그아웃과 충돌하지 않는다 (해제는 체크박스로) */
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token,
        initializing,
        sessionExpired,
        dismissSessionExpired,
        loginWithCredentials,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
